import { Button } from "@/components/flexkit/Button";
import { Text } from "@/components/podkit/typography/Text";
import { UserInputBlock_TextInput } from "gitpod-next-api/gitpod/v1/agent_pb";
import { UserInputBlock } from "gitpod-next-api/gitpod/v1/agent_pb";
import { useSendToAgentExecution, type PlainAgentExecution } from "@/queries/agent-queries";
import { SendToAgentExecutionRequest } from "gitpod-next-api/gitpod/v1/agent_pb";
import { type FC, useState, type ChangeEvent, useEffect, useMemo } from "react";
import { useBranch } from "@/hooks/use-branch";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { ChatBubble } from "@/components/ai/chat/ChatBubble";
import type { Message, UserMessage } from "@/hooks/use-stream-conversation";
import { cn } from "@/components/podkit/lib/cn";
import { EnvironmentOnboardingActions } from "@/components/environments/onboarding/EnvironmentOnboardingActions";
import { findMessageWithActions } from "./actions-for-message";
import { ExternalLink } from "@/components/podkit/typography/Link";

// eslint-disable-next-line react-refresh/only-export-components
export function generateCommitMessage(branchName: string): string {
    return `Please ensure you're on branch "${branchName}", commit the changes, and push them to the remote`;
}

// eslint-disable-next-line react-refresh/only-export-components
export function isCommitMessage(message: UserMessage): boolean {
    const content = message.fragments.map((fragment) => fragment.content).join(" ");
    return /Please ensure you're on branch ".*", commit the changes, and push them to the remote/.test(content);
}

type ConversationMessageCommitFilesProps = {
    isAgentExecutionDone: boolean;
    agentExecution: PlainAgentExecution;
    environment: PlainEnvironment;
    messages: Message[];
};

export const ConversationMessageCommitFiles: FC<ConversationMessageCommitFilesProps> = ({
    isAgentExecutionDone,
    agentExecution,
    environment,
    messages,
}) => {
    const sendToAgentExecution = useSendToAgentExecution();
    const currentBranch = useBranch(environment);
    const [branchName, setBranchName] = useState<string>(currentBranch);
    const [hasSentMessage, setHasSentMessage] = useState<boolean>(false);

    const { userMessage, actions } = useMemo(() => {
        return findMessageWithActions(messages, isCommitMessage);
    }, [messages]);

    useEffect(() => {
        if (userMessage && !hasSentMessage) {
            setHasSentMessage(true);
        }
    }, [hasSentMessage, userMessage]);

    const askAgentToCommitFiles = async () => {
        const message = generateCommitMessage(branchName);
        await sendToAgentExecution.mutateAsync(
            new SendToAgentExecutionRequest({
                agentExecutionId: agentExecution.id,
                userInput: new UserInputBlock({
                    input: {
                        case: "text",
                        value: new UserInputBlock_TextInput({
                            content: message,
                        }),
                    },
                }),
            }),
        );
        setHasSentMessage(true);
    };

    // If we haven't sent a message yet, and the agent is still working, it means that
    // it isn't done with the previous step yet, so we don't show the message.
    if (!isAgentExecutionDone && !userMessage && !hasSentMessage) {
        return null;
    }

    return (
        <div data-testid="conversation-message-commit-files" className="contents">
            <Text className="text-base">
                I have finished configuring the development environment. To review the changes I&apos;ve made look at
                the edits above, or open the environment in your editor. If this is your first time using Gitpod you
                might find our documentation for{" "}
                <ExternalLink href="https://www.gitpod.io/docs/gitpod/introduction/devcontainer">
                    Development Containers
                </ExternalLink>{" "}
                and{" "}
                <ExternalLink href="https://www.gitpod.io/docs/gitpod/introduction/automations">
                    Automations
                </ExternalLink>{" "}
                helpful.
            </Text>
            <Text className="text-base">
                You&apos;ll need to add the configuration to the repository so it can be reused by environments in the
                future. I can commit and push the changes to any branch you&apos;d like.
            </Text>
            <div
                className={cn("flex flex-col gap-2", {
                    hidden: !isAgentExecutionDone || userMessage || hasSentMessage,
                })}
            >
                <InputField label="Branch" className="grow">
                    <Input
                        value={branchName}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setBranchName(e.target.value)}
                        placeholder="Enter branch name"
                        className="max-w-full grow"
                    />
                </InputField>
                <Button
                    variant="primary"
                    onClick={askAgentToCommitFiles}
                    disabled={hasSentMessage || sendToAgentExecution.isPending}
                    className="w-fit"
                >
                    Commit & push
                </Button>
            </div>
            {userMessage && <ChatBubble message={userMessage} agentExecution={agentExecution} isLast={true} />}
            {actions.length > 0 && (
                <EnvironmentOnboardingActions
                    isAgentExecutionDone={isAgentExecutionDone}
                    agentExecution={agentExecution}
                    actions={actions}
                    title={{
                        completed: "Pushed changes",
                        inProgress: "Committing & pushing changes",
                    }}
                />
            )}
        </div>
    );
};
